/**
 * Book-scoped assistant chat threads (Cursor-style: many threads, not tied to chapter).
 */

export type DesktopChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  scope: "chapter" | "book";
  chapterId: string | null;
  createdAt: string;
};

export type DesktopChatThread = {
  id: string;
  title: string;
  messages: DesktopChatMessage[];
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
};

export type DesktopAssistantState = {
  chatThreads: DesktopChatThread[];
  activeChatThreadId: string | null;
  storyCraftByChapter: Record<string, unknown>;
  /** @deprecated migrated into chatThreads */
  chapterChats?: Record<string, DesktopChatMessage[]>;
  /** @deprecated */
  bookChat?: DesktopChatMessage[];
};

/** Max messages kept per thread (avoids unbounded growth in book metadata / recovery JSON). */
export const MAX_MESSAGES_PER_THREAD = 100;
/** Max chat threads stored on the book. */
export const MAX_CHAT_THREADS = 50;

function nowIso(): string {
  return new Date().toISOString();
}

function trimThreadMessages(messages: DesktopChatMessage[]): DesktopChatMessage[] {
  if (messages.length <= MAX_MESSAGES_PER_THREAD) return messages;
  return messages.slice(-MAX_MESSAGES_PER_THREAD);
}

function trimChatThreads(threads: DesktopChatThread[]): DesktopChatThread[] {
  const trimmed = threads.map((thread) => ({
    ...thread,
    messages: trimThreadMessages(thread.messages),
  }));
  if (trimmed.length <= MAX_CHAT_THREADS) return trimmed;
  return trimmed.slice(0, MAX_CHAT_THREADS);
}

export function createEmptyChatThread(): DesktopChatThread {
  const at = nowIso();
  return {
    id: `chat-thread-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    title: "New chat",
    messages: [],
    createdAt: at,
    updatedAt: at,
  };
}

function cloneChatMessages(value: unknown): DesktopChatMessage[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: DesktopChatMessage[] = [];
  for (const message of value) {
    if (!message || typeof message !== "object") continue;
    const cloned = { ...(message as DesktopChatMessage) };
    const messageId = typeof cloned.id === "string" ? cloned.id : "";
    const signature =
      messageId ||
      `${cloned.role ?? "unknown"}:${cloned.chapterId ?? "none"}:${cloned.createdAt ?? "unknown"}:${cloned.content ?? ""}`;
    if (seen.has(signature)) continue;
    seen.add(signature);
    out.push(cloned);
  }
  return out;
}

function normalizeThread(raw: unknown): DesktopChatThread | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Partial<DesktopChatThread>;
  if (typeof t.id !== "string" || !t.id.trim()) return null;
  const messages = cloneChatMessages(t.messages);
  const createdAt = typeof t.createdAt === "string" ? t.createdAt : nowIso();
  const updatedAt =
    typeof t.updatedAt === "string"
      ? t.updatedAt
      : messages[messages.length - 1]?.createdAt ?? createdAt;
  const title =
    typeof t.title === "string" && t.title.trim() ? t.title.trim() : "New chat";
  const archivedAt =
    typeof t.archivedAt === "string" && t.archivedAt.trim() ? t.archivedAt : undefined;
  return { id: t.id, title, messages, createdAt, updatedAt, archivedAt };
}

export function isBlankChatThread(thread: DesktopChatThread): boolean {
  return thread.messages.length === 0;
}

function titleFromFirstUserMessage(messages: DesktopChatMessage[]): string {
  const first = messages.find((m) => m.role === "user" && m.content.trim());
  if (!first) return "New chat";
  const line = first.content.trim().split(/\n/)[0] ?? "";
  const trimmed = line.replace(/\s+/g, " ").trim();
  if (!trimmed) return "New chat";
  return trimmed.length > 56 ? `${trimmed.slice(0, 56)}…` : trimmed;
}

export function normalizeDesktopAssistantState(
  raw: Partial<DesktopAssistantState> | undefined,
  chapterTitleById: Map<string, string>
): DesktopAssistantState {
  const storyCraftByChapter =
    raw?.storyCraftByChapter && typeof raw.storyCraftByChapter === "object"
      ? { ...(raw.storyCraftByChapter as Record<string, unknown>) }
      : {};

  const fromThreads: DesktopChatThread[] = [];
  if (Array.isArray(raw?.chatThreads)) {
    for (const item of raw.chatThreads) {
      const thread = normalizeThread(item);
      if (thread) fromThreads.push(thread);
    }
  }

  if (fromThreads.length === 0) {
    const chapterChats =
      raw?.chapterChats && typeof raw.chapterChats === "object"
        ? (raw.chapterChats as Record<string, unknown>)
        : {};
    for (const [chapterId, messages] of Object.entries(chapterChats)) {
      const cloned = cloneChatMessages(messages);
      if (cloned.length === 0) continue;
      const chapterTitle = chapterTitleById.get(chapterId);
      const at = cloned[0]?.createdAt ?? nowIso();
      const updated = cloned[cloned.length - 1]?.createdAt ?? at;
      fromThreads.push({
        id: `migrated-${chapterId}`,
        title: chapterTitle ? `Chat — ${chapterTitle}` : "Chat",
        messages: cloned,
        createdAt: at,
        updatedAt: updated,
      });
    }

    const legacyBook = cloneChatMessages(raw?.bookChat);
    if (legacyBook.length > 0) {
      const at = legacyBook[0]?.createdAt ?? nowIso();
      fromThreads.push({
        id: "migrated-book-chat",
        title: "Book chat",
        messages: legacyBook,
        createdAt: at,
        updatedAt: legacyBook[legacyBook.length - 1]?.createdAt ?? at,
      });
    }
  }

  fromThreads.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));

  let foundBlank = false;
  let chatThreads = fromThreads.filter((thread) => {
    if (!isBlankChatThread(thread)) return true;
    if (thread.archivedAt || foundBlank) return false;
    foundBlank = true;
    return true;
  });
  let activeChatThreadId =
    typeof raw?.activeChatThreadId === "string" ? raw.activeChatThreadId : null;

  if (chatThreads.length === 0) {
    const thread = createEmptyChatThread();
    chatThreads = [thread];
    activeChatThreadId = thread.id;
  } else if (
    !activeChatThreadId ||
    !chatThreads.some((t) => t.id === activeChatThreadId && !t.archivedAt)
  ) {
    activeChatThreadId = chatThreads.find((thread) => !thread.archivedAt)?.id ?? null;
    if (!activeChatThreadId) {
      const thread = createEmptyChatThread();
      chatThreads = [thread, ...chatThreads];
      activeChatThreadId = thread.id;
    }
  }

  chatThreads = trimChatThreads(chatThreads);
  if (
    !activeChatThreadId ||
    !chatThreads.some((t) => t.id === activeChatThreadId && !t.archivedAt)
  ) {
    activeChatThreadId = chatThreads.find((thread) => !thread.archivedAt)?.id ?? null;
  }

  return {
    chatThreads,
    activeChatThreadId,
    storyCraftByChapter,
  };
}

export function getActiveChatThread(state: DesktopAssistantState): DesktopChatThread | null {
  const activeThreads = state.chatThreads.filter((thread) => !thread.archivedAt);
  if (!state.activeChatThreadId) return activeThreads[0] ?? null;
  return (
    activeThreads.find((thread) => thread.id === state.activeChatThreadId) ??
    activeThreads[0] ??
    null
  );
}

export function appendChatMessage(
  state: DesktopAssistantState,
  threadId: string,
  message: DesktopChatMessage
): DesktopAssistantState {
  const hasId = state.chatThreads.some((t) => t.messages.some((m) => m.id === message.id));
  if (hasId) return state;

  const chatThreads = state.chatThreads.map((thread) => {
    if (thread.id !== threadId) return thread;
    const messages = trimThreadMessages([...thread.messages, message]);
    const title =
      thread.title === "New chat" && message.role === "user"
        ? titleFromFirstUserMessage(messages)
        : thread.title;
    return {
      ...thread,
      title,
      messages,
      updatedAt: message.createdAt || nowIso(),
    };
  });

  return { ...state, chatThreads: trimChatThreads(chatThreads), activeChatThreadId: threadId };
}

export function clearChatThread(state: DesktopAssistantState, threadId: string): DesktopAssistantState {
  const cleared = state.chatThreads.map((thread) =>
    thread.id === threadId
      ? { ...thread, messages: [], title: "New chat", updatedAt: nowIso(), archivedAt: undefined }
      : thread
  );
  return {
    ...state,
    chatThreads: cleared.filter(
      (thread) => thread.id === threadId || !isBlankChatThread(thread)
    ),
    activeChatThreadId: threadId,
  };
}

export function addChatThread(state: DesktopAssistantState, thread: DesktopChatThread): DesktopAssistantState {
  const existingBlank = state.chatThreads.find(
    (candidate) => !candidate.archivedAt && isBlankChatThread(candidate)
  );
  if (existingBlank) {
    return { ...state, activeChatThreadId: existingBlank.id };
  }
  return {
    ...state,
    chatThreads: trimChatThreads([thread, ...state.chatThreads]),
    activeChatThreadId: thread.id,
  };
}

export function archiveChatThread(
  state: DesktopAssistantState,
  threadId: string
): DesktopAssistantState {
  const target = state.chatThreads.find((thread) => thread.id === threadId);
  if (!target || target.archivedAt || isBlankChatThread(target)) return state;
  const archivedAt = nowIso();
  let chatThreads = state.chatThreads.map((thread) =>
    thread.id === threadId ? { ...thread, archivedAt, updatedAt: archivedAt } : thread
  );
  let nextActive = chatThreads.find((thread) => !thread.archivedAt)?.id ?? null;
  if (!nextActive) {
    const blank = createEmptyChatThread();
    chatThreads = [blank, ...chatThreads];
    nextActive = blank.id;
  }
  return {
    ...state,
    chatThreads: trimChatThreads(chatThreads),
    activeChatThreadId:
      state.activeChatThreadId === threadId ? nextActive : state.activeChatThreadId,
  };
}

export function restoreChatThread(
  state: DesktopAssistantState,
  threadId: string
): DesktopAssistantState {
  if (!state.chatThreads.some((thread) => thread.id === threadId && thread.archivedAt)) {
    return state;
  }
  return {
    ...state,
    chatThreads: state.chatThreads.map((thread) =>
      thread.id === threadId ? { ...thread, archivedAt: undefined, updatedAt: nowIso() } : thread
    ),
    activeChatThreadId: threadId,
  };
}
